<script setup>
import { computed, ref } from 'vue'
import AppInput from '@/components/atoms/AppInput'
import AppSkeleton from '@/components/atoms/AppSkeleton'
import HelpHubChatRows from './HelpHubChatRows.vue'
import { useSupportStore } from '@/stores/SupportStore'
import { useHelpHub } from '@/hooks/useHelpHub'
import { filterConversations, filterQuestions, splitConversations, ticketDigits } from '@/helpers/support'
import { HELP_HUB_CHAT_PAGE, HELP_HUB_TAB } from '@/constants'

/**
 * One of the Chat tab's "View all" pages: every past conversation, ticket or popular
 * question, under a field that filters the rows where they stand (Figma 29807:70192 /
 * 70193 / 70194). A row opens its conversation on the Chat tab, where the thread is drawn.
 */
const props = defineProps({
  page: { type: String, required: true, validator: (page) => Object.values(HELP_HUB_CHAT_PAGE).includes(page) },
})

// The empty lines are ours: the frames draw none.
const PAGES = {
  [HELP_HUB_CHAT_PAGE.CONVERSATIONS]: { title: 'Previous conversations', placeholder: 'Search for a conversation', empty: 'No previous conversations yet.' },
  [HELP_HUB_CHAT_PAGE.TICKETS]: { title: 'Support tickets', placeholder: 'Search for a ticket', empty: 'No support tickets yet.' },
  [HELP_HUB_CHAT_PAGE.QUESTIONS]: { title: 'Popular questions', placeholder: 'Search for a question', empty: 'No questions to show yet.' },
}

const supportStore = useSupportStore()
const { openHelp, openTicket } = useHelpHub()

const query = ref('')

const copy = computed(() => PAGES[props.page])
const isQuestions = computed(() => props.page === HELP_HUB_CHAT_PAGE.QUESTIONS)
const all = computed(() => {
  if (isQuestions.value) return supportStore.questions
  const split = splitConversations(supportStore.conversations)
  return props.page === HELP_HUB_CHAT_PAGE.TICKETS ? split.tickets : split.previous
})
const items = computed(() => (isQuestions.value ? filterQuestions(all.value, query.value) : filterConversations(all.value, query.value)))
// Both lists load together, so the conversations' flag stands for the questions' too.
const loading = computed(() => supportStore.conversationsLoading && !all.value.length)
const noMatch = computed(() => (query.value.trim() ? `Nothing matches “${query.value.trim()}”.` : copy.value.empty))

// Opened from a link, before the landing ever loaded the lists.
if (!supportStore.conversations.length && !supportStore.questions.length) supportStore.load()

// The thread is drawn on the Chat tab, so a row takes its conversation there through the URL, which names its ticket; a question is asked there.
const select = (item) => {
  if (isQuestions.value) {
    supportStore.send(item)
    return openHelp(HELP_HUB_TAB.CHAT)
  }

  const number = ticketDigits(item)
  if (number) return openTicket(number)

  // Without a number the URL cannot name it, so it opens in place.
  supportStore.openConversation(item.id)
  return openHelp(HELP_HUB_TAB.CHAT)
}
</script>

<template>
  <section class="flex flex-col gap-y-4 px-4 pt-3 pb-6">
    <!-- Figma sets the field 12px under the title and the rows 16px under the field. -->
    <header class="flex flex-col gap-y-3">
      <h3 class="text-base font-semibold text-gray-900" v-text="copy.title" />

      <AppInput
        v-model="query"
        size="md"
        leading-icon="heroicons-outline:search"
        :placeholder="copy.placeholder"
        :aria-label="copy.placeholder"
        clearable
      />
    </header>

    <div v-if="loading" role="status" class="flex flex-col gap-y-4">
      <span class="sr-only">Loading</span>
      <AppSkeleton v-for="row in 3" :key="row" height="h-[72px]" rounded="rounded-xl" />
    </div>

    <HelpHubChatRows v-else-if="items.length" :list="page" :items="items" @select="select" />

    <p v-else class="text-sm text-gray-500" role="status" v-text="noMatch" />
  </section>
</template>
